import { Injectable, inject } from '@angular/core';
import { SharedUiThemingCssVariablesService } from '../shared-ui-theming-css-variables.service';
import {
    generateBackgroundColorCssVariable,
    generateColorCssVariable,
    generateColoredBackgroundColorCssVariable,
    generateColoredTextColorCssVariable,
    generateStrokeColorCssVariable,
    generateTextColorCssVariable,
    generateTextOnColoredBackgroundColorCssVariable
} from './shared-ui-color-classes.utils';
import { SharedUiColorsService } from './shared-ui-colors.service';
import {
    coloredBackgroundColorDerivations,
    SharedUiBackgroundColorDerivation,
    SharedUiBackgroundColorName,
    SharedUiColorDerivation,
    SharedUiColorName,
    SharedUiTextColorDerivation
} from './shared-ui-colors.types';

@Injectable({
    providedIn: 'root'
})
export class SharedUiColorsCssVariablesAutoUpdateService {
    private _themingCssVariablesService = inject(SharedUiThemingCssVariablesService);
    private _colorsService = inject(SharedUiColorsService);

    private _isEnabled = false;

    enableColorCssVariablesAutoUpdate() {
        if (this._isEnabled) {
            console.error('You tried to enable the color css variables auto update multiple times');
            return;
        }
        this._isEnabled = true;

        for (const colorName of Object.values(SharedUiColorName)) {
            for (const derivation of Object.values(SharedUiColorDerivation)) {
                this._colorsService.getColor$(colorName, { derivation }).subscribe(color => {
                    this._themingCssVariablesService.setPrecomputedThemingCssVariable(
                        generateColorCssVariable(colorName, derivation),
                        color
                    );
                });
            }
        }

        for (const backgroundColor of Object.values(SharedUiBackgroundColorName)) {
            for (const derivation of Object.values(SharedUiBackgroundColorDerivation)) {
                this._colorsService.getBackgroundColor$({ color: backgroundColor, derivation }).subscribe(generatedColor => {
                    this._themingCssVariablesService.setPrecomputedThemingCssVariable(
                        generateBackgroundColorCssVariable({ color: backgroundColor, derivation }),
                        generatedColor
                    );
                });

                for (const textDerivation of Object.values(SharedUiTextColorDerivation)) {
                    this._colorsService.getTextColor$(textDerivation, { color: backgroundColor, derivation }).subscribe(generatedColor => {
                        this._themingCssVariablesService.setPrecomputedThemingCssVariable(
                            generateTextColorCssVariable(textDerivation, { color: backgroundColor, derivation }),
                            generatedColor
                        );
                    });
                }

                for (const color of Object.values(SharedUiColorName).filter(
                    c => !(<SharedUiColorName[]>[SharedUiColorName.PRIMARY, SharedUiColorName.SECONDARY]).includes(c)
                )) {
                    this._colorsService.getColoredTextColor$(color, { color: backgroundColor, derivation }).subscribe(generatedColor => {
                        this._themingCssVariablesService.setPrecomputedThemingCssVariable(
                            generateColoredTextColorCssVariable(color, { color: backgroundColor, derivation }),
                            generatedColor
                        );
                    });
                }

                this._colorsService.getStrokeColor$({ color: backgroundColor, derivation }).subscribe(generatedColor => {
                    this._themingCssVariablesService.setPrecomputedThemingCssVariable(
                        generateStrokeColorCssVariable({ color: backgroundColor, derivation }),
                        generatedColor
                    );
                });
            }
        }

        for (const backgroundColor of Object.values(SharedUiColorName).filter(
            c => !(<SharedUiColorName[]>[SharedUiColorName.PRIMARY, SharedUiColorName.SECONDARY]).includes(c)
        )) {
            for (const derivation of coloredBackgroundColorDerivations) {
                this._colorsService.getColoredBackgroundColor$({ color: backgroundColor, derivation }).subscribe(generatedColor => {
                    this._themingCssVariablesService.setPrecomputedThemingCssVariable(
                        generateColoredBackgroundColorCssVariable({ color: backgroundColor, derivation }),
                        generatedColor
                    );
                });

                this._colorsService.getTextColorOnColoredBackground$({ color: backgroundColor, derivation }).subscribe(generatedColor => {
                    this._themingCssVariablesService.setPrecomputedThemingCssVariable(
                        generateTextOnColoredBackgroundColorCssVariable({ color: backgroundColor, derivation }),
                        generatedColor
                    );
                });
            }
        }
    }
}
